/**
 * AI Utilities
 * 
 * Provider-agnostic utility functions for AI-related operations.
 * This module extends the legacy chatgptUtils.ts with multi-model support.
 */

import { AIMessage, AIResponse, AIProvider, AIModelConfig } from '../services/AIProviderService';
import { ChatGptMessage, ChatGptResponse, ChatGptRole } from '../services/StoryGeneratorService/ChatGpt.types';

/**
 * Convert ChatGptMessage array to AIMessage array
 * Used for backward compatibility with existing code
 */
export const convertToAIMessages = (messages: ChatGptMessage[]): AIMessage[] => {
  return messages.map(msg => ({
    role: msg.role === ChatGptRole.System ? 'system' as const :
          msg.role === ChatGptRole.User ? 'user' as const : 'assistant' as const,
    content: msg.content
  }));
};

/**
 * Convert AIResponse to ChatGptResponse
 * Used for backward compatibility with existing code
 */
export const convertToChatGptResponse = (response: AIResponse): ChatGptResponse => {
  return {
    id: `ai-${Date.now()}`,
    object: 'chat.completion',
    created: Math.floor(Date.now() / 1000),
    model: response.model,
    usage: {
      prompt_tokens: response.usage.promptTokens,
      completion_tokens: response.usage.completionTokens,
      total_tokens: response.usage.totalTokens
    },
    choices: [{
      message: {
        role: 'assistant',
        content: response.content
      },
      finish_reason: response.finishReason,
      index: 0
    }]
  };
};

/**
 * Format a message with placeholder substitution
 * Replaces {0}, {1}, etc. with provided values
 */
export const formatAIMessage = (
  message: string,
  values: string[],
  role: 'system' | 'user' | 'assistant' = 'user'
): AIMessage => {
  let formattedMessage = message;
  values.forEach((value, index) => {
    formattedMessage = formattedMessage.replace(`{${index}}`, value);
  });
  return {
    role,
    content: formattedMessage
  };
};

/**
 * Get provider display name for UI
 */
export const getProviderDisplayName = (provider: AIProvider): string => {
  const displayNames: Record<AIProvider, string> = {
    [AIProvider.OPENAI]: 'OpenAI',
    [AIProvider.ANTHROPIC]: 'Anthropic',
    [AIProvider.GOOGLE]: 'Google',
    [AIProvider.XAI]: 'xAI'
  };
  return displayNames[provider] || provider;
};

/**
 * Get provider icon/logo identifier
 */
export const getProviderIcon = (provider: AIProvider): string => {
  const icons: Record<AIProvider, string> = {
    [AIProvider.OPENAI]: 'openai',
    [AIProvider.ANTHROPIC]: 'anthropic',
    [AIProvider.GOOGLE]: 'google',
    [AIProvider.XAI]: 'xai'
  };
  return icons[provider] || 'default';
};

/**
 * Group models by provider for display in dropdowns
 */
export const groupModelsByProvider = (
  models: AIModelConfig[]
): Record<AIProvider, AIModelConfig[]> => {
  return models.reduce((acc, model) => {
    if (!acc[model.provider]) {
      acc[model.provider] = [];
    }
    acc[model.provider].push(model);
    return acc;
  }, {} as Record<AIProvider, AIModelConfig[]>);
};

/**
 * Get the recommended model for a provider or overall
 */
export const getRecommendedModel = (
  models: AIModelConfig[],
  provider?: AIProvider
): AIModelConfig | undefined => {
  const filtered = provider 
    ? models.filter(m => m.provider === provider)
    : models;
  return filtered.find(m => m.isRecommended) || filtered[0];
};

/**
 * Format model display name with provider prefix
 */
export const formatModelDisplayName = (
  model: AIModelConfig,
  includeProvider = false
): string => {
  if (includeProvider) {
    return `${getProviderDisplayName(model.provider)} - ${model.displayName}`;
  }
  return model.displayName;
};

/**
 * Check if a model supports a specific capability
 */
export const modelSupportsCapability = (
  model: AIModelConfig,
  capability: string
): boolean => {
  return model.capabilities.includes(capability as AIModelConfig['capabilities'][number]);
};

/**
 * Get models that support JSON mode (required for structured generation)
 */
export const getJsonModeModels = (models: AIModelConfig[]): AIModelConfig[] => {
  return models.filter(m => m.supportsJsonMode);
};

/**
 * Estimate token count (rough approximation)
 * Uses the rule of thumb that 1 token ≈ 4 characters
 */
export const estimateTokenCount = (text: string): number => {
  return Math.ceil(text.length / 4);
};

/**
 * Count words in a prompt (splits on non-alphanumeric separators).
 */
export const countWords = (text: string): number => {
  return text.split(/[^a-zA-Z0-9-_]+/).filter(Boolean).length;
};

/**
 * Check if content fits within model's context window
 */
export const contentFitsInContext = (
  content: string,
  model: AIModelConfig,
  reserveTokens = 4096 // Reserve space for response
): boolean => {
  const estimatedTokens = estimateTokenCount(content);
  return estimatedTokens < (model.maxTokens - reserveTokens);
};
